import React from 'react';
import { type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';

interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'success' | 'warning' | 'error' | 'info' | 'gray' | 'brand';
  size?: 'sm' | 'md' | 'lg';
  icon?: LucideIcon;
  dot?: boolean;
}

export const Badge: React.FC<BadgeProps> = ({
  className,
  variant = 'gray',
  size = 'md',
  icon: Icon,
  dot = false,
  children,
  ...props
}) => {
  const variants = {
    brand: "bg-brand-primary/10 text-brand-primary border-brand-primary/20",
    success: "bg-green-500/10 text-green-500 border-green-500/20",
    warning: "bg-amber-500/10 text-amber-500 border-amber-500/20",
    error: "bg-red-500/10 text-red-500 border-red-500/20",
    info: "bg-blue-500/10 text-blue-500 border-blue-500/20",
    gray: "bg-white/5 text-gray-400 border-white/10"
  };

  const sizes = {
    sm: "px-2 py-0.5 text-[8px] gap-1",
    md: "px-2.5 py-1 text-[9px] gap-1.5",
    lg: "px-3.5 py-1.5 text-[10px] gap-2"
  };

  const dotColors = {
    brand: "bg-brand-primary",
    success: "bg-green-500",
    warning: "bg-amber-500",
    error: "bg-red-500",
    info: "bg-blue-500",
    gray: "bg-gray-500"
  };

  return (
    <div
      className={cn(
        "inline-flex items-center font-black uppercase tracking-[0.1em] border transition-all duration-300 rounded-full select-none shrink-0",
        variants[variant],
        sizes[size],
        className
      )}
      {...props}
    >
      {dot && <span className={cn("w-1.5 h-1.5 rounded-full animate-pulse", dotColors[variant])} />}
      {Icon && <Icon size={size === 'sm' ? 10 : 12} />}
      {children}
    </div>
  );
};
